import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Lean dark-mode service for the shared shell.
 *
 * Mirrors the ESSENTIALS of the main portal's ThemeService — toggles the `dark`
 * class on `<html>` (the shared-ui Tailwind preset is `darkMode: ['class']`, so all
 * shared-ui components react), persists the choice, and falls back to the OS
 * preference on first visit — WITHOUT pulling the portal's ThemeService (which also
 * owns high-contrast / color-blind toggles the shell apps don't need yet).
 *
 * Uses the same `dark_mode` localStorage key as the main portal for consistency.
 * The shell apps run on their own origin (different port/subdomain), so storage is
 * not actually shared — the matching key just keeps behaviour identical.
 */
@Injectable({ providedIn: 'root' })
export class ShellThemeService {
  private readonly STORAGE_KEY = 'dark_mode';
  private readonly mediaQuery = '(prefers-color-scheme: dark)';

  private darkSubject = new BehaviorSubject<boolean>(false);
  readonly isDarkMode$: Observable<boolean> = this.darkSubject.asObservable();

  constructor() {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    const systemPrefersDark = window.matchMedia(this.mediaQuery).matches;
    // Guard the parse: the service is root-provided and eagerly constructed in the
    // pre-paint APP_INITIALIZER, so a non-JSON value (e.g. devtools tampering) must
    // NOT throw and abort app bootstrap — fall back to the OS preference instead.
    const isDark = stored !== null ? this.parseStored(stored, systemPrefersDark) : systemPrefersDark;
    this.apply(isDark, false);

    // Follow OS-level changes only while the user hasn't made an explicit choice.
    window.matchMedia(this.mediaQuery).addEventListener('change', (e) => {
      if (localStorage.getItem(this.STORAGE_KEY) === null) this.apply(e.matches, false);
    });
  }

  /** Robustly parse the persisted `dark_mode` flag; any non-boolean/garbage value
   *  falls back to the OS preference rather than throwing during bootstrap. */
  private parseStored(stored: string, fallback: boolean): boolean {
    try {
      const value = JSON.parse(stored);
      return typeof value === 'boolean' ? value : fallback;
    } catch {
      return fallback;
    }
  }

  get isDarkMode(): boolean {
    return this.darkSubject.value;
  }

  toggle(): void {
    this.apply(!this.darkSubject.value, true);
  }

  setDarkMode(isDark: boolean): void {
    this.apply(isDark, true);
  }

  private apply(isDark: boolean, persist: boolean): void {
    document.documentElement.classList.toggle('dark', isDark);
    if (persist) localStorage.setItem(this.STORAGE_KEY, JSON.stringify(isDark));
    this.darkSubject.next(isDark);
  }
}
